import React, { Component } from 'react';
import Downshift from 'downshift';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash-es/debounce';
import { defineMessages, injectIntl } from 'react-intl';
import Button from '../../Button';
import Item from './Item';

const messages = defineMessages({
  Search: 'Search',
  LoadingProgress: 'Loading...',
  ErrorLoadingData: 'Error loading data',
  NoItems: 'No items',
  Clear: 'Clear',
  StartTypingToSearch: 'Start typing to search',
});

class AutoComplete extends Component {
  hadFocus = false;

  static propTypes = {
    placeholder: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    emptyLabel: PropTypes.string,
    loadFunc: PropTypes.func,
    // Trigger load function when component is loaded (with empty value)
    loadOnInit: PropTypes.bool,
    // Trigger load function with an empty value when the input is cleared (instead of emptying the list)
    reloadOnClear: PropTypes.bool,
    initialSelectedItem: PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
    value: PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
    items: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired,
        disabled: PropTypes.bool,
      }),
    ),
    autoFocus: PropTypes.bool,
    canClear: PropTypes.bool,
    isSmall: PropTypes.bool,
    menuRight: PropTypes.bool,

    /** can be auto, edit or readOnly. auto will make the input togglable between edit and readOnly */
    mode: PropTypes.string,
    /** Errormessage and indicator if element should render in error state */
    error: PropTypes.string,
    /** Handle onFocus events */
    onFocus: PropTypes.func,
    /** Handle onBlur events */
    onBlur: PropTypes.func,
    /** Callback that is called when user clears selection */
    onClear: PropTypes.func,
    /** Specify custom filter to search items */
    filterFunc: PropTypes.func,
    /** Specify custom classname for items */
    itemClassNameFunc: PropTypes.func /* (item)=>classname string or object */,
    inputGroupItemLeft: PropTypes.node,
  };

  static defaultProps = {
    placeholder: null,
    disabled: false,
    value: null,
    emptyLabel: null,
    items: null,
    loadFunc: undefined,
    mode: 'auto',
    error: undefined,
    initialSelectedItem: undefined,
    autoFocus: false,
    canClear: true,
    loadOnInit: false,
    reloadOnClear: false,
    onFocus: () => {},
    onBlur: () => {},
    onClear: () => {},
    filterFunc: (item, inputValue) =>
      !inputValue || (item.label && item.label.toUpperCase().includes(inputValue.toUpperCase())),
    itemClassNameFunc: undefined,
    isSmall: false,
    inputGroupItemLeft: null,
  };

  constructor(props) {
    super(props);
    const { items } = props;

    this.state = {
      inputValue: null,
      loading: false,
      hasError: false,
      items,
      mode: props.mode === 'auto' ? 'readOnly' : props.mode,
      currentInputValue: '',
    };
  }

  /* ----------------------------------------------------------------------------------- */

  renderInput() {
    const { mode: stateMode } = this.state;
    const { mode, value, intl, autoFocus } = this.props;
    const readOnly = stateMode !== 'edit';
    const className = classNames({
      'TextInput--readOnly': readOnly,
      'form-control TextInput--edit': !readOnly,
      'TextInput--editable': mode === 'auto' && readOnly,
    });

    const propsContainer = {
      className,
      placeholder: intl.formatMessage(messages.Search),
      value: value ? value.label : '',
      readOnly,
      onFocus: this.handleFocus,
      onBlur: this.handleInputBlur,
      ref: input => {
        this.inputElement = input;
      },
      autoFocus,
    };

    return <input {...propsContainer} />;
  }

  handleInputBlur = event => {
    // Don't blur when the dropdown button is clicked
    if (event.relatedTarget && event.relatedTarget.id === 'dropdownButton') return;

    const { onBlur, mode, error } = this.props;

    if (error) {
      // When there is an error set focus back to input
      event.target.focus();
      return;
    }
    if (this.inputElement) {
      this.inputElement.blur();
    }

    this.setState(state => ({
      ...state,
      mode: mode === 'auto' ? 'readOnly' : mode,
    }));

    onBlur(event);
  };

  handleFocus = event => {
    const { onFocus, mode } = this.props;

    this.setState(state => ({
      ...state,
      mode: mode === 'auto' ? 'edit' : mode,
    }));

    onFocus(event);
  };

  componentDidUpdate(prevProps) {
    const { error, items } = this.props;

    if (prevProps.error !== error && error) {
      this.inputElement.focus();
    }

    if (prevProps.items !== items) {
      this.setState(state => ({
        ...state,
        items,
      }));
    }
  }
  componentDidMount() {
    const { loadFunc, loadOnInit } = this.props;
    if ((loadFunc, loadOnInit)) {
      const { inputValue } = this.state;
      this.setState({ loading: true });
      this.debouncedLoadFunc(inputValue);
    }
  }

  /* ----------------------------------------------------------------------------------- */

  renderTypeahead({ getInputProps, getToggleButtonProps, clearSelection, selectHighlightedItem }) {
    const {
      disabled,
      onClear,
      intl,
      placeholder,
      autoFocus,
      onChange,
      canClear,
      loadFunc,
      reloadOnClear,
      inputRef,
      isSmall,
      inputGroupItemLeft,
    } = this.props;
    const { items } = this.state;
    this.hadFocus = false;

    const inputProps = getInputProps({
      placeholder: placeholder || intl.formatMessage(messages.Search),
      onBlur: this.handleInputBlur,
      onChange: e => {
        if (e.target.value === '') {
          clearSelection(); // Call downshift method
          onClear(); // Call props method to propogate to upper component
          if (loadFunc) {
            if (reloadOnClear) {
              this.setState({ loading: true, inputValue: null });
              this.debouncedLoadFunc('');
            } else {
              this.setState({ items: [] });
            }
          }
        } else {
          const inputValueFoundInList =
            items && items.length > 0 && items.map(x => x.label).includes(e.target.value);
          if (!inputValueFoundInList && loadFunc) {
            this.loadItems(e.target.value);
          }
        }
      },
      onKeyDown: e => {
        if (e.key === 'Tab') {
          selectHighlightedItem();
        }
      },
      ref: input => {
        this.inputElement = input;
      },
    });

    return (
      <div className={classNames({ 'input-group': true }, { 'input-group-sm': isSmall })}>
        {inputGroupItemLeft}
        <input
          className="form-control"
          {...inputProps}
          disabled={disabled}
          ref={inputRef}
          autoFocus={autoFocus}
          data-1p-ignore
          type="text"
        />

        {/* if we cant clear the value, we want to always show a dropdown button so that another value can be selected */}
        {(!inputProps.value || !canClear) && (
          <span className="input-group-btn">
            <Button
              btnClassName=""
              id="dropdownButton"
              disabled={disabled}
              ref={buttonDOM => {
                this.dropdownButtonRef = buttonDOM;
              }}
              {...getToggleButtonProps()}
            >
              <span className="caret" />
            </Button>
          </span>
        )}

        {!disabled && canClear && inputProps.value && (
          <span className="input-group-btn">
            <Button
              btnClassName="clearButton"
              disabled={disabled}
              title={intl.formatMessage(messages.Clear)}
              onClick={() => {
                clearSelection(); // Call downshift method
                onClear(); // Call props method to propogate to upper component
                onChange(null);
              }}
            >
              <span className="fa fa-times" />
            </Button>
          </span>
        )}
      </div>
    );
  }

  renderMenu({ isOpen, getMenuProps, selectedItem, highlightedIndex, getItemProps, inputValue }) {
    const { items, loading, hasError } = this.state;
    const {
      loadFunc,
      intl,
      filterFunc,
      canClear,
      renderItem,
      itemClassNameFunc,
      menuRight,
    } = this.props;

    let filteredItems = items;

    const inputValueFoundInList =
      items && items.length > 0 && items.map(x => x.label).includes(inputValue);

    // if load function was passed, filtering is done by backend
    // otherwise, filter the items based on its label
    if (
      (isOpen &&
        items &&
        items.length > 0 &&
        !loadFunc &&
        // we dont want to filter the selection list when a value was selected and the clear button is not present (only a dropdown button to select another value)
        canClear) ||
      (!canClear && !inputValueFoundInList)
    ) {
      filteredItems = filteredItems.filter(item => filterFunc(item, inputValue));
    }

    return (
      <div className={classNames({ open: isOpen }, { 'Select--relative': true })}>
        <ul
          {...getMenuProps()}
          className={classNames('Select--dropdown dropdown-menu', {
            'dropdown-menu-right': menuRight,
          })}
          style={{ overflowX: 'auto' }}
        >
          {isOpen &&
            filteredItems &&
            filteredItems.length > 0 &&
            filteredItems.map((item, index) => (
              <Item
                // This is needed because sometimes the same item will be present in the item collection but with a different label
                // Example: the task filters: the user dropdown will contain 'assigned to me' and 'Pim Pelmoes'. Both with the same key
                key={`${index}-${item.key}`}
                item={item}
                index={index}
                isSelected={highlightedIndex === index}
                isActive={selectedItem === item}
                getItemProps={getItemProps}
                renderItem={renderItem}
                searchValue={inputValue}
                disabled={item.disabled}
                className={itemClassNameFunc ? itemClassNameFunc(item) : undefined}
              />
            ))}

          {isOpen && (
            <Item item={null} getItemProps={getItemProps}>
              {loading && intl.formatMessage(messages.LoadingProgress)}
              {!loading && hasError && intl.formatMessage(messages.errorLoadingData)}
              {!loading &&
                !hasError &&
                (!filteredItems || filteredItems.length < 1) &&
                inputValue &&
                intl.formatMessage(messages.NoItems)}
              {!loading &&
                !hasError &&
                (!filteredItems || filteredItems.length < 1) &&
                !inputValue &&
                intl.formatMessage(messages.StartTypingToSearch)}
            </Item>
          )}
        </ul>
      </div>
    );
  }

  // debounce makes sure that if this method is called again within 300 ms, only the last call is executed.
  // This is used to ignore user input when typing the next letter within 300 ms.
  debouncedLoadFunc = debounce(newInputValue => {
    const { loadFunc } = this.props;
    this.setState({ loading: true });
    loadFunc(newInputValue)
      .then(items => {
        this.setState(state => ({
          ...state,
          inputValue: newInputValue,
          loading: false,
          hasError: false,
          items,
        }));
      })
      .catch(() => {
        this.setState(state => ({
          ...state,
          inputValue: newInputValue,
          loading: false,
          hasError: true,
        }));
      });
  }, 1000);

  loadItems = newInputValue => {
    const { inputValue } = this.state;
    if (inputValue === newInputValue || !newInputValue) return;
    this.setState({ loading: true });
    this.debouncedLoadFunc(newInputValue);
  };

  onSelectedItemChanged(item) {
    const { onChange } = this.props;
    onChange(item);
  }

  render() {
    const { value, error, initialSelectedItem } = this.props;
    const { mode: stateMode } = this.state;
    const readOnly = stateMode !== 'edit';
    const classname = classNames({
      'form-group': true,
      'has-error has-feedback': error,
      'has-feedback': !value,
    });

    if (readOnly) {
      return (
        <div className={classname} title={error}>
          {this.renderInput()}
        </div>
      );
    }

    return (
      <Downshift
        onChange={selectedItem => this.onSelectedItemChanged(selectedItem)}
        onStateChange={this.handleStateChange}
        initialSelectedItem={initialSelectedItem}
        selectedItem={value || initialSelectedItem || null}
        itemToString={item => (item ? item.label : '')}
      >
        {downshiftProps => (
          <div>
            {this.renderTypeahead(downshiftProps)}
            {this.renderMenu(downshiftProps)}
          </div>
        )}
      </Downshift>
    );
  }
}

export default injectIntl(AutoComplete);
